import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import ApplicationForm from './components/applications/ApplicationForm';
import ApplicationList from './components/applications/ApplicationList';
import Login from './components/auth/Login';
import SetupAccount from './components/auth/SetupAccount';
import LazyRoute from './components/common/LazyRoute';
import RateLimitErrorBoundary from './components/common/RateLimitErrorBoundary';
import EndpointList from './components/endpoints/EndpointList';
import ModernLayout from './components/layout/ModernLayout';
import CreateRole from './components/roles/CreateRole';
import RoleEdit from './components/roles/RoleEdit';
import RoleList from './components/roles/RoleList';
import UserList from './components/users/UserList';
import { useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import WorkflowList from './features/workflows/WorkflowList';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import './utils/cssOptimizer';
import './utils/performanceValidator';
import './utils/bundleSplitValidation';

// Lazier routes to reduce initial bundle size
const ApiDocViewer = lazy(() => import('./components/documentation/ApiDocViewer'));
const RateLimitAnalytics = lazy(() => import('./components/rateLimits/RateLimitAnalytics'));
const RateLimitForm = lazy(() => import('./components/rateLimits/RateLimitForm'));
const RateLimitList = lazy(() => import('./components/rateLimits/RateLimitList'));
const RateLimitMonitor = lazy(() => import('./components/rateLimits/RateLimitMonitor'));
const EndpointWizard = lazy(() => import('./components/services/EndpointWizard'));
const ServiceForm = lazy(() => import('./components/services/ServiceForm'));
// Phase 1: Lazy load top 5 routes for code splitting
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'));
const WorkflowBuilder = lazy(() => import('./features/workflows/WorkflowBuilder'));
const ServiceList = lazy(() => import('./components/services/ServiceList'));
const ConnectionList = lazy(() => import('./components/connections/ConnectionList'));

// Reports components (grouped as one logical unit)
const WorkflowExecutionReport = lazy(() => import('./components/reports/WorkflowExecutionReport'));
const ApiTrafficReport = lazy(() => import('./components/reports/ApiTrafficReport'));

// Lazy-load settings screens to reduce initial bundle size
const AdminSettings = lazy(() => import('./components/settings/AdminSettings'));
const UserSettings = lazy(() => import('./components/settings/UserSettings'));

// BreadcrumbProvider is scoped to the protected layout: its only consumers are
// ModernTopBar (rendered by ModernLayout) and protected pages (WorkflowBuilder, RoleEdit).
const ProtectedLayout = ({ children }) => {
  return (
    <BreadcrumbProvider>
      <ModernLayout>{children}</ModernLayout>
    </BreadcrumbProvider>
  );
};

const ProtectedRoute = ({ children, requiredPermission }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && !user[requiredPermission]) {
    return <Navigate to="/dashboard" replace />;
  }

  return <ProtectedLayout>{children}</ProtectedLayout>;
};

function App() {
  const { user } = useAuth();
  const isAuthenticated = !!user;

  // Enable session timeout management
  useSessionTimeout();

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <NotificationProvider>
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
          />

          <Route path="/setup-account" element={<SetupAccount />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <LazyRoute component={Dashboard} routeName="Dashboard" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/connections"
            element={
              <ProtectedRoute>
                <LazyRoute component={ConnectionList} routeName="Connections" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services"
            element={
              <ProtectedRoute>
                <LazyRoute component={ServiceList} routeName="Services" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/wizard"
            element={
              <ProtectedRoute>
                <LazyRoute component={EndpointWizard} routeName="Endpoint Wizard" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/new"
            element={
              <ProtectedRoute>
                <LazyRoute component={ServiceForm} routeName="New Service" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/edit/:id"
            element={
              <ProtectedRoute>
                <LazyRoute component={ServiceForm} routeName="Edit Service" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <LazyRoute component={UserList} routeName="Users" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <ProtectedRoute>
                <LazyRoute component={RoleList} routeName="Roles" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/create"
            element={
              <ProtectedRoute>
                <CreateRole mode="create" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/edit/:id"
            element={
              <ProtectedRoute>
                <RoleEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications"
            element={
              <ProtectedRoute>
                <LazyRoute component={ApplicationList} routeName="Applications" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications/new"
            element={
              <ProtectedRoute>
                <ApplicationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/applications/edit/:id"
            element={
              <ProtectedRoute>
                <ApplicationForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workflows"
            element={
              <ProtectedRoute>
                <WorkflowList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workflows/edit/:id"
            element={
              <ProtectedRoute>
                <LazyRoute component={WorkflowBuilder} routeName="Workflow Builder" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documentation"
            element={
              <ProtectedRoute>
                <LazyRoute component={ApiDocViewer} routeName="API Documentation" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/workflow-executions"
            element={
              <ProtectedRoute>
                <LazyRoute
                  component={WorkflowExecutionReport}
                  routeName="Workflow Execution Report"
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/api-traffic"
            element={
              <ProtectedRoute>
                <LazyRoute component={ApiTrafficReport} routeName="API Traffic Report" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/endpoints"
            element={
              <ProtectedRoute requiredPermission="isAdmin">
                <EndpointList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-settings"
            element={
              <ProtectedRoute>
                <LazyRoute component={UserSettings} routeName="User Settings" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-settings"
            element={
              <ProtectedRoute requiredPermission="isAdmin">
                <LazyRoute component={AdminSettings} routeName="Admin Settings" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-limits"
            element={
              <ProtectedRoute requiredPermission="isAdmin">
                <RateLimitErrorBoundary>
                  <RateLimitList />
                </RateLimitErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-limits/monitor"
            element={
              <ProtectedRoute requiredPermission="isAdmin">
                <RateLimitErrorBoundary>
                  <RateLimitMonitor />
                </RateLimitErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-limits/analytics"
            element={
              <ProtectedRoute requiredPermission="isAdmin">
                <RateLimitErrorBoundary>
                  <RateLimitAnalytics />
                </RateLimitErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-limits/create"
            element={
              <ProtectedRoute requiredPermission="isAdmin">
                <RateLimitErrorBoundary>
                  <RateLimitForm />
                </RateLimitErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-limits/:id/edit"
            element={
              <ProtectedRoute requiredPermission="isAdmin">
                <RateLimitErrorBoundary>
                  <RateLimitForm />
                </RateLimitErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </NotificationProvider>
    </LocalizationProvider>
  );
}

export default App;
